import { Fire, Sun, Drop, Snowflake, Prohibit, CheckCircle } from "@phosphor-icons/react";

/**
 * LeadStatusChip — the canonical rendering of `crm_leads.lead_status`.
 *
 * Drip Health printed this column as a bare grey outline chip, so every
 * lead looked identical no matter how cold it had gone. The heat bands
 * already have an agreed palette — `backend/heat_utils.py` emits
 * red → amber → teal → blue, and `HeatChip` paints it — but that
 * component keys on a computed `{band, days}` payload the drip-health
 * endpoint does not return. This one keys on the raw status string.
 *
 * Three properties this deliberately holds to:
 *
 * 1. **Theme-independent.** The chip paints its own opaque background,
 *    so its contrast is fixed regardless of the page background. The
 *    app's dark presets (Midnight / Charcoal / Navy in `lib/themes.js`)
 *    swap `--theme-page-bg` to a near-black; a translucent tint would
 *    lose its contrast there, and white-on-amber — what `HeatChip` uses
 *    — is only 3.1:1 and fails AA at this text size in either theme.
 *    Every pairing below is a deep foreground on a pale tint, ≥ 7:1.
 *
 * 2. **Never colour alone.** The band name is always spelled out, and
 *    each band carries a distinct icon, so the chip still reads for a
 *    colour-blind operator, in greyscale print, and in a brochure
 *    screenshot.
 *
 * 3. **Honest about the unknown.** A status outside the heat vocabulary
 *    (`invalid_email`, `unsubscribed`, `closed`, …) gets a neutral
 *    treatment rather than being forced into a band it does not belong
 *    to. Inventing a colour for an unrecognised value is how a screen
 *    starts lying.
 */

const BANDS = {
  hot: {
    label: "Hot", icon: Fire,
    bg: "#FEF2F2", fg: "#991B1B", border: "#FCA5A5",
    hint: "Activity within the last 10 days",
  },
  warm: {
    label: "Warm", icon: Sun,
    bg: "#FFFBEB", fg: "#92400E", border: "#FCD34D",
    hint: "Last activity 11–21 days ago",
  },
  cool: {
    label: "Cool", icon: Drop,
    bg: "#F0FDFA", fg: "#115E59", border: "#5EEAD4",
    hint: "Last activity 22–60 days ago",
  },
  cold: {
    label: "Cold", icon: Snowflake,
    bg: "#EFF6FF", fg: "#1E40AF", border: "#93C5FD",
    hint: "No activity for more than 60 days",
  },
};

// Terminal / administrative statuses. Not heat — deliberately muted so
// they never compete with the bands for attention.
const TERMINAL = {
  invalid_email: { label: "Invalid email", icon: Prohibit, hint: "Address bounced — not mailable" },
  unsubscribed: { label: "Unsubscribed", icon: Prohibit, hint: "Opted out of marketing" },
  do_not_contact: { label: "Do not contact", icon: Prohibit, hint: "Flagged by sales" },
  inactive: { label: "Inactive", icon: Prohibit, hint: "No longer worked" },
  closed: { label: "Closed", icon: CheckCircle, hint: "Opportunity closed" },
  closed_lost: { label: "Closed lost", icon: Prohibit, hint: "Opportunity lost" },
  lost: { label: "Lost", icon: Prohibit, hint: "Opportunity lost" },
};

const NEUTRAL = { bg: "#F5F5F5", fg: "#374151", border: "#D4D4D4" };

export default function LeadStatusChip({ status, size = "sm" }) {
  const key = (status || "").toString().trim().toLowerCase();
  const band = BANDS[key];
  const terminal = !band ? TERMINAL[key] : null;

  if (!band && !terminal) {
    // Unknown or absent. An em dash, not a coloured guess.
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]"
            data-testid="lead-status-chip-unknown">
        {status || "—"}
      </span>
    );
  }

  const cfg = band || { ...NEUTRAL, ...terminal };
  const Icon = cfg.icon;
  const pad = size === "xs" ? "px-1.5 py-0.5" : "px-2 py-1";
  const font = size === "xs" ? "text-[9px]" : "text-[10px]";

  return (
    <span
      className={`inline-flex items-center gap-1 ${pad} ${font} font-bold uppercase tracking-wider border whitespace-nowrap`}
      style={{ backgroundColor: cfg.bg, color: cfg.fg, borderColor: cfg.border }}
      title={`${cfg.label} — ${cfg.hint}`}
      data-testid={`lead-status-chip-${key}`}
    >
      <Icon size={11} weight="bold" aria-hidden="true" />
      {cfg.label}
    </span>
  );
}

export { BANDS as LEAD_STATUS_BANDS, TERMINAL as LEAD_STATUS_TERMINAL };
